// Stripe Checkout for the recruiter Pro plan.
//
// The server creates the checkout session (POST /billing/checkout/) for the
// signed-in owner and we send the browser to Stripe's hosted page. Nothing here
// grants a plan: the Stripe webhook does that on the server a few seconds after
// the invoice is paid. Stripe sends the person back to
// /dashboard/agency?checkout=success, where we poll GET /billing/status/ until
// the account reads Pro.
//
// NEXT_PUBLIC_STRIPE_CHECKOUT ("on" or empty) is baked in at build time like the
// other NEXT_PUBLIC_* vars. Empty leaves Upgrade on the /contact fallback.

import { API_URL } from "@/components/config/config";
import fetchWithAuth from "@/hooks/fetchWithAuth";
import { clearProfileCache } from "@/hooks/useFetchUserDetails";
import { emitJourneyEvent } from "@/lib/analytics/beacon";
import { EVENTS } from "@/lib/analytics/events";

const PRO_OFFER_CODE = "recruiter_99";
const STATUS_POLL_INTERVAL_MS = 2000;
const STATUS_POLL_WINDOW_MS = 60_000;

/** True when this build sells Pro through Stripe Checkout. */
export function stripeEnabled(): boolean {
  return process.env.NEXT_PUBLIC_STRIPE_CHECKOUT === "on";
}

export type CheckoutStart = "redirected" | "already_subscribed";

/** Which Upgrade button was clicked; the admin Payments tab filters on it. */
export type UpgradePlacement = "sidebar" | "plan_card" | "pricing" | "pricing_auto";

/** Ask the server for a checkout link and go there. Throws when none comes back. */
export async function startProCheckout(placement: UpgradePlacement): Promise<CheckoutStart> {
  emitJourneyEvent(EVENTS.CHECKOUT_STARTED, { offer_code: PRO_OFFER_CODE, provider: "stripe", placement });
  // omitUid: identity comes from the token, never from the body.
  const res = await fetchWithAuth(`${API_URL}/billing/checkout/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ placement }),
    omitUid: true,
  });
  if (res.status === 409) return "already_subscribed";
  const data = res.ok ? await res.json() : null;
  if (!data?.url) throw new Error("checkout unavailable");
  window.location.assign(data.url);
  return "redirected";
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Poll the server until the account reads Pro. False when the window runs out. */
async function waitForPro(): Promise<boolean> {
  const deadline = Date.now() + STATUS_POLL_WINDOW_MS;
  while (Date.now() < deadline) {
    try {
      const res = await fetchWithAuth(`${API_URL}/billing/status/`, { omitUid: true, retry: false });
      if (res.ok && (await res.json())?.offerCode === PRO_OFFER_CODE) return true;
    } catch {
      // A failed poll is retried until the window closes.
    }
    await sleep(STATUS_POLL_INTERVAL_MS);
  }
  return false;
}

let returnHandled = false;

/** On the page Stripe returns to: null when this is not a checkout return,
 *  otherwise whether the account reads Pro within a minute. Runs once per load,
 *  however many components ask, and drops the flag from the address bar. */
export async function finishCheckoutReturn(): Promise<boolean | null> {
  if (returnHandled || typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get("checkout") !== "success") return null;
  returnHandled = true;
  params.delete("checkout");
  const query = params.toString();
  window.history.replaceState(null, "", `${window.location.pathname}${query ? `?${query}` : ""}`);

  emitJourneyEvent(EVENTS.CHECKOUT_COMPLETED, { offer_code: PRO_OFFER_CODE, provider: "stripe" });
  const active = await waitForPro();
  if (active) clearProfileCache();
  return active;
}
